import React, { useState, useEffect } from 'react';
import { Table, Button, Form, message, Space, Popconfirm, Modal, InputNumber } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined, PercentageOutlined } from '@ant-design/icons';
import axios from 'axios';
import { API_BASE_URL } from '../constants/api';
import CategoryCreateModal from './CategoryCreateModal';
import SearchBar from './common/SearchBar';
import { useDebounce } from 'use-debounce';

const CategoryManagement = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [priceIncreaseModalVisible, setPriceIncreaseModalVisible] = useState(false);
  const [selectedCategoryForPrice, setSelectedCategoryForPrice] = useState(null);
  const [percentValue, setPercentValue] = useState(0);
  const [form] = Form.useForm();
  const [searchTerm, setSearchTerm] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);
  const [debouncedSearchTerm] = useDebounce(searchTerm, 300);

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    if (debouncedSearchTerm) {
      fetchCategories(debouncedSearchTerm);
    } else {
      fetchCategories();
    }
  }, [debouncedSearchTerm]);

  const fetchCategories = async (query = '') => {
    setSearchLoading(true);
    try {
      const params = query ? { Name: query } : {};
      const response = await axios.get(`${API_BASE_URL}Category/List`, { params });
      if (response.data.code === 0) {
        setCategories(response.data.data || []);
      } else {
        message.error(response.data.message || 'فشل في جلب الفئات');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'خطأ في جلب الفئات';
      message.error(errorMessage);
    }
    setSearchLoading(false);
    setLoading(false);
  };

  const handleCreate = () => {
    setEditingCategory(null);
    form.resetFields();
    setIsModalVisible(true);
  };

  const handleEdit = (record) => {
    setEditingCategory(record);
    form.setFieldsValue(record);
    setIsModalVisible(true);
  };

  const handleDelete = async (id) => {
    try {
      const response = await axios.delete(`${API_BASE_URL}Category/Delete/${id}`);
      if (response.data.code === 0) {
        message.success(response.data.message || 'تم حذف الفئة بنجاح');
        fetchCategories();
      } else {
        message.error(response.data.message || 'فشل في حذف الفئة');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'خطأ في حذف الفئة';
      message.error(errorMessage);
    }
  };

  const handleModalSuccess = () => {
    setIsModalVisible(false);
    fetchCategories();
  };

  const handleModalCancel = () => {
    setIsModalVisible(false);
  };

  const columns = [
    {
      title: 'معرف',
      dataIndex: 'id',
      key: 'Id',
    },
    {
      title: 'الاسم',
      dataIndex: 'name',
      key: 'Name',
    },
    {
      title: 'الفئة الأساسية',
      dataIndex: 'baseCategoryName',
      key: 'BaseCategoryName',
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (text, record) => (
        <Space size="middle">
          <Button type="link" icon={<EditOutlined />} onClick={() => handleEdit(record)} />
          <Button 
            type="link" 
            icon={<PercentageOutlined />} 
            onClick={() => {
              setSelectedCategoryForPrice(record);
              setPercentValue(0);
              setPriceIncreaseModalVisible(true);
            }}
          >
            زيادة
          </Button>
          <Popconfirm
            title="هل أنت متأكد من حذف هذه الفئة؟"
            onConfirm={() => handleDelete(record.id)}
            okText="نعم"
            cancelText="لا"
          >
            <Button type="link" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
          إضافة فئة
        </Button>
        <SearchBar
          onSearch={setSearchTerm}
          loading={searchLoading}
          placeholder="ابحث بالاسم..."
        />
      </div>
      <div className="table-responsive-wrapper">
        <Table
          columns={columns}
          dataSource={categories}
          rowKey="id"
          loading={loading || searchLoading}
          scroll={{ x: 'max-content' }}
          locale={{ emptyText: searchTerm ? 'لا توجد فئات بهذا الاسم' : 'لا توجد فئات' }}
        />
      </div>
      <CategoryCreateModal
        open={isModalVisible}
        onCancel={handleModalCancel}
        onSuccess={handleModalSuccess}
        form={form}
        categories={categories}
        editingCategory={editingCategory}
      />
      
      <Modal
        title="زيادة أسعار المواد"
        open={priceIncreaseModalVisible}
        onCancel={() => setPriceIncreaseModalVisible(false)}
        onOk={async () => {
          if (!selectedCategoryForPrice) {
            message.error('يرجى إدخال نسبة مئوية صحيحة');
            return;
          }
          try {
            const response = await axios.post(`${API_BASE_URL}Category/increase-prices`, {
              categoryId: selectedCategoryForPrice.id,
              percentValue: percentValue
            });
            if (response.data.code === 0) {
              message.success(response.data.message || 'تمت الزيادة بنجاح');
              setPriceIncreaseModalVisible(false);
              fetchCategories(); // Refresh list
            } else {
              message.error(response.data.message || 'فشل في زيادة الأسعار');
            }
          } catch (error) {
            const errorMessage = error.response?.data?.message || 'خطأ في زيادة الأسعار';
            message.error(errorMessage);
          }
        }}
        okText="تأكيد"
        cancelText="إلغاء"
      >
        <p>الفئة: <strong>{selectedCategoryForPrice?.name}</strong></p>
        <p>النسبة المئوية (%): </p>
        <InputNumber
          min={0.01}
          step={0.1}
          value={percentValue}
          onChange={setPercentValue}
          placeholder="مثال: 10.5"
          style={{ width: '100%', marginTop: 8 }}
        />
      </Modal>
    </div>
  );
};

export default CategoryManagement;
